import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Boxes,
  Building2,
  ChevronRight,
  CloudCog,
  Cpu,
  FileClock,
  Gauge,
  LogOut,
  Menu,
  Microchip,
  Satellite,
  UserCog,
  Users,
  Wifi,
  X,
} from 'lucide-react';
import { getUser, hasFullAccess, logout } from '../../utils/auth';
import MachineWorkspaceNav from './MachineWorkspaceNav';
import DeploymentWorkspaceNav from './DeploymentWorkspaceNav';
import styles from './DashboardLayout.module.scss';

const routeTitles = [
  [/^\/$/, 'Fleet overview'],
  [/^\/analytics/, 'Analytics'],
  [/^\/machines\/(cloud-sync|sync-log)/, 'Cloud synchronization'],
  [/^\/machines\/companies/, 'Companies'],
  [/^\/machines$/, 'Machine management'],
  [/^\/machine\/[^/]+\/incidents/, 'Incidents'],
  [/^\/machine\/[^/]+\/notes/, 'Machine notes'],
  [/^\/machine\/[^/]+\/imu-installation/, 'IMU installation'],
  [/^\/device\/[^/]+\/status/, 'Hub status'],
  [/^\/device\/[^/]+\/config/, 'Hub configuration'],
  [/^\/device\/[^/]+\/logs/, 'Hub logs'],
  [/^\/device\/[^/]+\/error-detect/, 'Error detection'],
  [/^\/app-update$/, 'App deployment'],
  [/^\/app-update\/[^/]+\/channels/, 'Update channels'],
  [/^\/app-update\/[^/]+\/devices/, 'App device assignment'],
  [/^\/app-update\/[^/]+\/statistics/, 'App version statistics'],
  [/^\/app-update\/[^/]+$/, 'App deployment'],
  [/^\/hub-ota/, 'Hub firmware'],
  [/^\/imu-ota/, 'IMU firmware'],
  [/^\/gnss-ota/, 'GNSS firmware'],
  [/^\/imu-production/, 'IMU production'],
  [/^\/user-management/, 'User management'],
  [/^\/account/, 'Account'],
];

const getPageTitle = pathname =>
  routeTitles.find(([pattern]) => pattern.test(pathname))?.[1] || 'Terna Hub Cloud';

function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!sidebarOpen) {
      return undefined;
    }
    const handleEscape = event => {
      if (event.key === 'Escape') {
        setSidebarOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    document.body.classList.add('navigation-open');
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.classList.remove('navigation-open');
    };
  }, [sidebarOpen]);

  // Fleet management, deployments and IMU production are hidden from the
  // company-scoped partner role.
  const fullAccess = hasFullAccess();
  const groups = [
    {
      label: 'Workspace',
      items: [
        { to: '/', label: 'Fleet overview', icon: Gauge, end: true },
        { to: '/analytics', label: 'Analytics', icon: BarChart3 },
      ],
    },
    ...(fullAccess
      ? [
          {
            label: 'Fleet',
            items: [
              { to: '/machines', label: 'Machines', icon: Boxes, end: true },
              { to: '/machines/companies', label: 'Companies', icon: Building2 },
              { to: '/machines/cloud-sync', label: 'Cloud sync', icon: FileClock },
            ],
          },
          {
            label: 'Deployments',
            items: [
              { to: '/app-update', label: 'App deployment', icon: CloudCog },
              { to: '/hub-ota', label: 'Hub firmware', icon: Wifi },
              { to: '/imu-ota', label: 'IMU firmware', icon: Cpu },
              { to: '/gnss-ota', label: 'GNSS firmware', icon: Satellite },
            ],
          },
        ]
      : []),
    {
      label: 'Operations',
      items: [
        ...(fullAccess
          ? [{ to: '/imu-production', label: 'IMU production', icon: Microchip }]
          : []),
        ...(user?.role === 'admin'
          ? [{ to: '/user-management', label: 'Users', icon: Users }]
          : []),
      ],
    },
  ].filter(group => group.items.length > 0);

  return (
    <div className={`${styles.shell} app-shell`}>
      <button
        type="button"
        aria-label="Close navigation"
        className={`${styles.backdrop} ${sidebarOpen ? styles.backdropVisible : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      <aside
        id="primary-navigation"
        className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}
      >
        <div className={styles.brandRow}>
          <button type="button" className={styles.brand} onClick={() => navigate('/')}>
            <img src="/images/logo.svg" alt="Terna Systems" />
          </button>
          <button
            type="button"
            className={styles.closeButton}
            aria-label="Close navigation"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={21} />
          </button>
        </div>

        <div className={styles.productLabel}>
          <span className={styles.productIcon}>
            <Cpu size={17} />
          </span>
          <span>
            <strong>Hub Cloud</strong>
            <small>Operations console</small>
          </span>
        </div>

        <nav className={styles.navigation} aria-label="Primary navigation">
          {groups.map(group => (
            <div className={styles.navGroup} key={group.label}>
              <p className={styles.groupLabel}>{group.label}</p>
              {group.items.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                  }
                >
                  <Icon size={18} strokeWidth={1.9} />
                  <span>{label}</span>
                  <ChevronRight className={styles.navChevron} size={15} />
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className={styles.account}>
          <div className={styles.avatar} aria-hidden="true">
            {(user?.username || 'U').slice(0, 1).toUpperCase()}
          </div>
          <div className={styles.accountDetails}>
            <strong>{user?.username || 'Unknown user'}</strong>
            <span>{user?.role || 'user'}</span>
          </div>
          <button
            type="button"
            onClick={() => navigate('/account')}
            className={styles.logoutButton}
            title="Account settings"
          >
            <UserCog size={18} />
          </button>
          <button type="button" onClick={logout} className={styles.logoutButton} title="Log out">
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      <div className={styles.workspace}>
        <header className={styles.topbar}>
          <button
            type="button"
            className={styles.menuButton}
            aria-label="Open navigation"
            aria-controls="primary-navigation"
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={22} />
          </button>
          <div className={styles.pageIdentity}>
            <span>Terna Hub Cloud</span>
            <strong>{getPageTitle(location.pathname)}</strong>
          </div>
          <div className={styles.topbarActions}>
            {user && (
              <button
                type="button"
                className={styles.userButton}
                onClick={() => navigate(user.role === 'admin' ? '/user-management' : '/account')}
              >
                <UserCog size={18} />
                <span>{user.username}</span>
              </button>
            )}
          </div>
        </header>

        <MachineWorkspaceNav />
        <DeploymentWorkspaceNav />

        <main className={styles.content}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default DashboardLayout;
